/**
 * Utility functions for managing grouped data
 */

import type { CollectorMeta } from './createCollector';

type CombineGroupDataParams = {
  sourceIndexes: number[];
  data: unknown[][];
  order?: (items: unknown[]) => unknown[];
};

/**
 * Combines data from multiple sources within a group
 * @param params - Configuration object for combining group data
 * @param params.sourceIndexes - Indexes of sources in the group
 * @param params.data - Array of data for each source
 * @param params.order - Optional ordering function
 * @returns Combined and ordered data
 */
export function combineGroupData({
  sourceIndexes,
  data,
  order
}: CombineGroupDataParams) {
  const combinedData = sourceIndexes.flatMap(
    (sourceIndex) => data[sourceIndex] || []
  );

  if (order) {
    return order(combinedData);
  }

  return combinedData;
}

/**
 * Checks if any source in a group has next data available
 * @param sourceIndexes - Indexes of sources in the group
 * @param getSourceHasNext - Function to check if source has next data
 * @returns True if any source has next data
 */
export function groupHasNext(
  sourceIndexes: number[],
  getSourceHasNext: (sourceIndex: number) => boolean
) {
  return sourceIndexes.some((sourceIndex) => getSourceHasNext(sourceIndex));
}

/**
 * Collects metadata from all sources in a group
 * @param sourceIndexes - Indexes of sources in the group
 * @param collectors - Array of collectors
 * @returns Array of metadata from sources in the group
 */
export function collectGroupMetadata(
  sourceIndexes: number[],
  collectors: Array<{ meta: CollectorMeta<unknown> } | undefined>
): CollectorMeta<unknown>[] {
  return sourceIndexes
    .map((sourceIndex) => collectors[sourceIndex]?.meta)
    .filter((meta): meta is CollectorMeta<unknown> => Boolean(meta));
}

/**
 * Creates a next function for a group that loads next data for all sources
 * @param sourceIndexes - Indexes of sources in the group
 * @param getSourceHasNext - Function to check if source has next data
 * @param getSourceNext - Function to get next data for a source
 * @returns Async function to load next data for the group
 */
export function createGroupNext(
  sourceIndexes: number[],
  getSourceHasNext: (sourceIndex: number) => boolean,
  getSourceNext: (sourceIndex: number) => () => Promise<void>
) {
  return async () => {
    const sources = sourceIndexes
      .filter((sourceIndex) => getSourceHasNext(sourceIndex))
      .map((sourceIndex) => getSourceNext(sourceIndex));

    for (const getNext of sources) {
      await getNext();
    }
  };
}

/**
 * Creates a reset function for a group that resets all sources
 * @param sourceIndexes - Indexes of sources in the group
 * @param getSourceReset - Function to reset a source
 * @returns Function to reset all sources in the group
 */
export function createGroupReset(
  sourceIndexes: number[],
  getSourceReset: (sourceIndex: number) => () => void
) {
  return () => {
    sourceIndexes.forEach((sourceIndex) => {
      getSourceReset(sourceIndex)();
    });
  };
}
